import { Link } from 'react-router-dom'
import { SEO, FAQSchema } from '@/components/seo'
import { ArrowRight, Camera, Sparkles, Check } from 'lucide-react'
import { Dropzone } from '@/components/dropzone/Dropzone'
import { buttonStyles } from '@/components/ui'
import { Stats, Testimonials, BeforeAfter } from '@/components/sections'

export function EnhanceRealism() {
  const faqItems = [
    {
      question: 'How does AI image realism enhancement work?',
      answer: 'We apply subtle adjustments that make AI images appear more photographic: adding realistic noise patterns, adjusting color response to match camera sensors, and introducing natural imperfections.',
    },
    {
      question: 'Will this make my AI images look like real photos?',
      answer: 'Our tool significantly increases photorealism by adding characteristics found in real camera photos. Results depend on the original image quality.',
    },
    {
      question: 'Does enhancement help bypass AI detection?',
      answer: 'Yes, adding realistic camera characteristics can help confuse AI classifiers that look for "too perfect" image qualities typical of AI generation.',
    },
    {
      question: 'What types of images work best?',
      answer: 'Photorealistic AI images (from Flux, Midjourney, DALL-E) benefit most. Artistic or stylized images may not need this treatment.',
    },
  ]

  return (
    <>
      <SEO
        title="AI Image Realism Enhancer - Make AI Photos Look Real"
        description="Enhance AI image realism. Make AI-generated images look like real camera photos. Add natural imperfections and camera characteristics. Free tool."
        canonical="/enhance-realism"
        keywords="ai image realism, make ai images realistic, photorealism enhancer, ai photo realistic"
      />
      <FAQSchema questions={faqItems} />

      {/* Hero */}
      <section className="section bg-gradient-to-b from-amber-50 via-white to-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full text-sm text-amber-700 font-medium mb-6">
              <Camera className="w-4 h-4" />
              Photorealism Enhancement
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Make AI Images{' '}
              <span className="text-amber-600">Look Real</span>
            </h1>

            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Add realistic camera characteristics to your AI-generated images.
              Natural imperfections, sensor noise, and photographic qualities.
            </p>
          </div>

          {/* Dropzone */}
          <div className="max-w-2xl mx-auto">
            <Dropzone onFileSelect={() => {}} />
          </div>

          {/* Features */}
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              Camera noise simulation
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              Natural color response
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              Subtle imperfections
            </div>
          </div>
        </div>
      </section>

      {/* Stats / Trusted By */}
      <Stats />

      {/* Before/After Examples */}
      <BeforeAfter />

      {/* Testimonials */}
      <Testimonials />

      {/* What We Add */}
      <section className="section bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What Makes Real Photos Different?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              AI images are often "too perfect." Real cameras introduce subtle characteristics we simulate.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                title: 'Sensor Noise',
                description: 'Real cameras produce subtle noise patterns, especially in shadows and low light',
                icon: '📷',
              },
              {
                title: 'Color Response',
                description: 'Camera sensors have unique color profiles that differ from AI "perfect" colors',
                icon: '🎨',
              },
              {
                title: 'Film Grain',
                description: 'Optional film grain simulation for analog photography aesthetics',
                icon: '🎞️',
              },
              {
                title: 'Lens Characteristics',
                description: 'Subtle vignetting and chromatic aberration found in real lenses',
                icon: '🔍',
              },
              {
                title: 'JPEG Artifacts',
                description: 'Real photos often have compression artifacts from camera processing',
                icon: '💾',
              },
              {
                title: 'Micro Imperfections',
                description: 'Tiny variations that break the "too clean" AI look',
                icon: '✨',
              },
            ].map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <span className="text-2xl mb-3 block">{item.icon}</span>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After concept */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
              The Difference
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4 text-center">AI Generated (Original)</h3>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>❌ Too clean and perfect</li>
                  <li>❌ No sensor noise</li>
                  <li>❌ Mathematically perfect colors</li>
                  <li>❌ No lens imperfections</li>
                  <li>❌ Easily detected as AI</li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6 border-2 border-green-200">
                <h3 className="font-semibold text-gray-900 mb-4 text-center">Enhanced (After)</h3>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>✅ Natural imperfections</li>
                  <li>✅ Realistic noise patterns</li>
                  <li>✅ Camera-like color response</li>
                  <li>✅ Subtle lens characteristics</li>
                  <li>✅ More likely to pass detection</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section bg-white">
        <div className="container">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Perfect For
          </h2>

          <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { title: 'Stock Photos', description: 'Make AI images blend with real stock' },
              { title: 'Product Mockups', description: 'Realistic product photography' },
              { title: 'Social Media', description: 'Posts that look photographed' },
              { title: 'Portfolio Work', description: 'Professional presentation' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">
              Frequently Asked Questions
            </h2>

            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <details key={index} className="group bg-white rounded-xl">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <span className="font-medium text-gray-900">{item.question}</span>
                    <span className="text-gray-400 group-open:rotate-180 transition-transform">
                      ▼
                    </span>
                  </summary>
                  <div className="px-6 pb-6 text-gray-600">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-amber-600">
        <div className="container text-center">
          <Sparkles className="w-10 h-10 text-white mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Enhance Your AI Images Now
          </h2>
          <p className="text-amber-100 mb-8 max-w-xl mx-auto">
            Add photorealistic qualities to your AI-generated images.
          </p>
          <Link
            to="/"
            className={buttonStyles({
              variant: 'secondary',
              size: 'lg',
              className: 'bg-white text-amber-600 hover:bg-gray-100',
            })}
          >
            Start Free
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  )
}
